/**
 * W3 collapsed history — derive a region's implied past from its present graph.
 *
 * History is deliberately not serialized into CityGraph and is never persisted:
 * it is a pure, cheap view over analytic province events, settlement population
 * fields, and parcel growth epochs. A cache may retain the returned value, but a
 * fresh derivation must always produce the same records in the same order.
 */
import { hashSeed } from "../rng.js";
import type { PlanetSampler } from "../universe.js";
import {
  LEVEL_PROVINCE, LEVEL_REGION, type CellId, cellCenterDir, dirToCell, packCell,
} from "./cells.js";
import type { CityGraph } from "./citygraph.js";
import {
  type SettlementSeed, type WorldEvent, eventSchedule, popCurve, prosperity,
  seedHash, settlementSeeds,
} from "./fields.js";
import { dirToLocal } from "./citygraph.js";
import { districtAt, settlementPopulationFor, sprawlSeeds } from "./growth.js";
import { settlementName } from "./settlementNames.js";

export type ChronicleKind = "founding" | "war" | "plague" | "boom" | "district-founded";

/** A materialized fact whose refs point at parcel indices in the source graph. */
export interface ChronicleEntry {
  yearsAgo: number;
  kind: ChronicleKind;
  /** Stable settlement subcell id (dealt k, or 1000+d for a metro-sprawl centre). */
  settlementK: number;
  /** CityGraph parcel indices that substantiate this entry. */
  refs: number[];
  regionId: CellId;
  summary: string;
}

export interface SettlementChronicle {
  /** Stable procedural identity; W4 NPC ids extend this string. */
  settlementId: string;
  seedK: number;
  tier: number;
  suitability: number;
  foundedYearsAgo: number;
  populationNow: number;
  prosperityNow: number;
  entries: ChronicleEntry[];
}

export interface RegionChronicle {
  regionId: CellId;
  provinceId: CellId;
  /** Province-wide analytic input, exposed so adjacent-region agreement is auditable. */
  provinceEvents: WorldEvent[];
  settlements: SettlementChronicle[];
  /** All settlement entries in canonical oldest-first order. */
  entries: ChronicleEntry[];
}

interface LocalSeed {
  u: number; v: number; k: number; tier: number; suit: number; deviation: number;
}

const FOUNDING_SEARCH_MAX_YEARS = 4096;
const FOUNDING_SEARCH_STEPS = 48;
const HISTORY_MILLIS = 1000;

/**
 * Invert popCurve's zero/non-zero boundary instead of duplicating its founding
 * formula. The rounded milliyear result is stable and substantially finer than
 * any player-facing date.
 */
export function invertFoundingEpoch(seedH: number, suitability: number): number {
  if (popCurve(seedH, suitability, 0) <= 0) return 0;
  let inhabited = 0;
  let uninhabited = FOUNDING_SEARCH_MAX_YEARS;
  for (let i = 0; i < FOUNDING_SEARCH_STEPS; i++) {
    const mid = (inhabited + uninhabited) * 0.5;
    if (popCurve(seedH, suitability, mid) > 0) inhabited = mid;
    else uninhabited = mid;
  }
  return Math.round(uninhabited * HISTORY_MILLIS) / HISTORY_MILLIS;
}

function copyCell(cell: CellId): CellId {
  return { face: cell.face, x: cell.x, y: cell.y, level: cell.level };
}

function localSeeds(sampler: PlanetSampler, graph: CityGraph): LocalSeed[] {
  const raw = settlementSeeds(sampler, graph.regionId);
  const xy: [number, number] = [0, 0];
  const out: LocalSeed[] = raw.map((seed: SettlementSeed) => {
    dirToLocal(graph.frame, seed.dx, seed.dy, seed.dz, xy);
    return {
      u: xy[0], v: xy[1], k: seed.k, tier: seed.tier,
      suit: seed.suit, deviation: seed.deviation,
    };
  });
  out.push(...sprawlSeeds(sampler, graph.regionId, graph.frame, raw));
  out.sort((a, b) => a.k - b.k);
  return out;
}

function parcelCentroid(graph: CityGraph, parcelIndex: number): [number, number] {
  const poly = graph.parcels[parcelIndex].poly;
  let u = 0, v = 0;
  const count = poly.length / 2;
  for (let i = 0; i < poly.length; i += 2) { u += poly[i]; v += poly[i + 1]; }
  return count > 0 ? [u / count, v / count] : [0, 0];
}

function parcelOwners(graph: CityGraph, seeds: readonly LocalSeed[]): number[][] {
  const owned = seeds.map((): number[] => []);
  for (let p = 0; p < graph.parcels.length; p++) {
    const [u, v] = parcelCentroid(graph, p);
    let owner = 0;
    let best = Infinity;
    for (let s = 0; s < seeds.length; s++) {
      const du = u - seeds[s].u, dv = v - seeds[s].v;
      const d2 = du * du + dv * dv;
      if (d2 < best) { best = d2; owner = s; }
    }
    if (seeds.length > 0) owned[owner].push(p);
  }
  return owned;
}

function constructionYearsAgo(founded: number, epoch: number, minEpoch: number, maxEpoch: number): number {
  // Construction occupies the middle 75% of the settlement's lifetime. Lower
  // growth epochs are older; equal-epoch settlements receive one middle date.
  const rank = maxEpoch > minEpoch ? (epoch - minEpoch) / (maxEpoch - minEpoch) : 0.5;
  const years = founded * (0.85 - 0.75 * rank);
  return Math.round(years * HISTORY_MILLIS) / HISTORY_MILLIS;
}

function entryOrder(a: ChronicleEntry, b: ChronicleEntry): number {
  const kindOrder: Record<ChronicleKind, number> = {
    founding: 0, war: 1, plague: 2, boom: 3, "district-founded": 4,
  };
  return (b.yearsAgo - a.yearsAgo)
    || (a.settlementK - b.settlementK)
    || (kindOrder[a.kind] - kindOrder[b.kind])
    || ((a.refs[0] ?? -1) - (b.refs[0] ?? -1));
}

/**
 * Collapse one already-materialized region into a deterministic implied history.
 * The graph is read-only and its serialized bytes are untouched.
 */
export function collapseRegion(sampler: PlanetSampler, graph: CityGraph): RegionChronicle {
  if (graph.regionId.level !== LEVEL_REGION) {
    throw new Error("collapseRegion: CityGraph must belong to a region cell");
  }

  const centre = new Float64Array(3);
  cellCenterDir(sampler.def.radius, graph.regionId, centre);
  const provinceId = dirToCell(
    sampler.def.radius, LEVEL_PROVINCE, centre[0], centre[1], centre[2],
  );
  const planetHash = hashSeed(sampler.def.seed);
  const provinceEvents = eventSchedule(planetHash, provinceId);
  const seeds = localSeeds(sampler, graph);
  const owned = parcelOwners(graph, seeds);
  const settlements: SettlementChronicle[] = [];

  for (let s = 0; s < seeds.length; s++) {
    const seed = seeds[s];
    const settlementId = `${sampler.def.seed}:stl:${graph.regionId.face}:${graph.regionId.x}:${graph.regionId.y}:${seed.k}`;
    const name = settlementName(settlementId);
    const sh = seedHash(planetHash, graph.regionId, seed.k);
    const foundedYearsAgo = invertFoundingEpoch(sh, seed.suit);
    const entries: ChronicleEntry[] = [{
      yearsAgo: foundedYearsAgo,
      kind: "founding",
      settlementK: seed.k,
      refs: [],
      regionId: copyCell(graph.regionId),
      summary: `${name} was founded.`,
    }];

    for (const event of provinceEvents) {
      if (event.yearsAgo >= foundedYearsAgo) continue;
      entries.push({
        yearsAgo: event.yearsAgo,
        kind: event.kind,
        settlementK: seed.k,
        refs: [],
        regionId: copyCell(graph.regionId),
        summary: `A ${event.kind} reshaped ${name}.`,
      });
    }

    const parcelRefs = owned[s];
    let minEpoch = Infinity, maxEpoch = -Infinity;
    for (const p of parcelRefs) {
      const epoch = graph.parcels[p].epoch;
      if (epoch < minEpoch) minEpoch = epoch;
      if (epoch > maxEpoch) maxEpoch = epoch;
    }
    const districts = new Map<number, number[]>();
    for (const p of parcelRefs) {
      const [u, v] = parcelCentroid(graph, p);
      const district = districtAt(planetHash, packCell(graph.regionId), [seed], seed.tier, u, v).id;
      const refs = districts.get(district);
      if (refs === undefined) districts.set(district, [p]); else refs.push(p);
    }
    for (const district of [...districts.keys()].sort((a, b) => a - b)) {
      const refs = districts.get(district)!;
      refs.sort((a, b) => a - b);
      let epoch = Infinity;
      for (const ref of refs) if (graph.parcels[ref].epoch < epoch) epoch = graph.parcels[ref].epoch;
      entries.push({
        yearsAgo: constructionYearsAgo(foundedYearsAgo, epoch, minEpoch, maxEpoch),
        kind: "district-founded",
        settlementK: seed.k,
        refs,
        regionId: copyCell(graph.regionId),
        summary: `District ${district} of ${name} took shape.`,
      });
    }

    entries.sort(entryOrder);
    settlements.push({
      settlementId,
      seedK: seed.k,
      tier: seed.tier,
      suitability: seed.suit,
      foundedYearsAgo,
      // The analytic curve establishes founding/history. Present census follows
      // the grown settlement tier metadata that was explicitly calibrated for W4;
      // otherwise a three-building hamlet can inherit a province-scale population.
      populationNow: settlementPopulationFor(sampler.def.seed, graph.regionId, seed.tier, seed.k),
      prosperityNow: prosperity(sh, seed.suit, 0, provinceEvents),
      entries,
    });
  }

  settlements.sort((a, b) => a.seedK - b.seedK);
  const entries = settlements.flatMap((settlement) => settlement.entries).sort(entryOrder);
  const regionId = copyCell(graph.regionId);
  return {
    regionId,
    provinceId: copyCell(provinceId),
    provinceEvents: provinceEvents.map((event) => ({ ...event })),
    settlements,
    entries,
  };
}
